import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { createTheme, TextField, ThemeProvider, useTheme } from "@mui/material";
import { DatePicker as Picker } from "@mui/x-date-pickers/";
import { useField, useFormikContext } from "formik";
import moment from "moment";
import { forwardRef, useState } from "react";
import * as yup from "yup";

export const modifyDetails = ({ minAge, maxAge }) =>
  yup.object({
    dob: yup
      .string()
      .required("Date of Birth is required")
      .min(14, "Please enter a valid date")
      .test("dob", "Age should be between 18 and 65", (value) => {
        const age = moment().diff(moment(value, "DD/MM/YYYY"), "years");
        return age >= minAge && age <= maxAge;
      }),
  });
const DatePicker = forwardRef(
  (
    {
      name,
      extraMsg,
      error,
      isNominee = false,
      readOnly,
      selectOnly,
      openPickerIcon,
      placeholder,
      setNomineeAge,
      noAgeWrapper = false,
      isProposer = false,
      ...props
    },
    ref
  ) => {
    const [field, meta] = useField(name);

    const calculateAge = (date) => {
      const today = moment();
      const dob = moment(date);

      const years = today.diff(dob, "years");

      if (years >= 1) {
        return { years, months: null };
      } else {
        const months = today.diff(dob, "months");
        return { years: null, months };
      }
    };

    const { setFieldValue, values } = useFormikContext();

  
    let tempDate = ["dob", "trip_start_date", "trip_end_date"].includes(name)
      ? values?.[name]
      : name.includes("nominee")
      ? values?.[name.split(".")[0]]?.nominee_dob
      : name.includes("specify")
      ? values?.[name.split(".")[0]]?.specify?.sub_qtn1
      : name.includes("appointee")
      ? values?.[name.split(".")[0]]?.appointee_dob
      : values?.[name.split(".")[0]]?.dob;

    const initialAge = tempDate
      ? calculateAge(moment(tempDate).format("YYYY-MM-DD"))
      : null;

    const [age, setAge] = useState(initialAge ? initialAge?.years : null);

    const [ageInMonths, setAgeInMonths] = useState(
      initialAge ? initialAge?.months : null
    );
    const onChange = (val) => {
      const { years, months } = calculateAge(val);

      if (years !== null) {
        setAge(years);
        setAgeInMonths(null);
        setNomineeAge &&
          isNominee &&
          setNomineeAge({ unit: "years", value: years });
      } else {
        setAgeInMonths(months);
        setAge(null);

        setNomineeAge &&
          isNominee &&
          setNomineeAge({ unit: "months", value: months });
      }

      setFieldValue(name, moment(val).format("YYYY-MM-DD"));
    };

    const parentTheme = useTheme();

    const config = {
      inputFormat: "DD/MM/YYYY",
      ...field,
      ...props,
      fullWidth: true,
      onChange,
      value: tempDate ? tempDate : null,
      placeholder: isProposer ? "" : placeholder || "DD/MM/YYYY",
    };

    if (meta && meta.touched && meta.error) {
      config.error = true;
      config.helperText = meta.error;
    }
    const getCssVariableValue = (variableName) =>
      getComputedStyle(document.documentElement)
        .getPropertyValue(variableName)
        .trim();
    const theme = createTheme({
      palette: {
        ...parentTheme.palette,
        primary: {
          ...parentTheme.palette.primary,
          main: getCssVariableValue("var(--primary-color)") || "#0a87ff",
        },
        secondary: {
          ...parentTheme.palette.secondary,
          main: getCssVariableValue("var(--primary-color)") || "#0a87ff",
        },
      },
      components: {
        MuiCalendarPicker: {
          styleOverrides: {
            root: {
              color: "var(--primary-color) !important",
              fontFamily: "var(--primary-font) !important",
              "& .MuiYearPicker-root": {
                "& .Mui-selected": {
                  backgroundColor: "var(--primary-color) !important",
                  color: "#fff !important",
                },
              },
              "& .MuiMonthPicker-root": {
                color: "var(--primary-color) !important",
                "& .Mui-selected": {
                  backgroundColor: "var(--primary-color) !important",
                  color: "#fff !important",
                },
                "& .Mui-disabled": {
                  color: "var(--primary-color) !important",
                },
              },
              "& .MuiDayPicker-root": {
                color: "var(--primary-color) !important",
                "& .Mui-selected": {
                  backgroundColor: "var(--primary-color) !important",
                  color: "#fff !important",
                },
              },
            },
          },
        },
        MuiPickersDay: {
          styleOverrides: {
            root: {
              color: "var(--primary-color)",
              fontFamily: "var(--primary-font) !important",
              "&.Mui-selected": {
                backgroundColor: "var(--primary-color) !important",
                color: "#fff !important",
              },
            },
          },
        },
        MuiPickersYear: {
          styleOverrides: {
            root: {
              fontFamily: "var(--primary-font) !important",
            },
          },
        },
        MuiPickersMonth: {
          styleOverrides: {
            root: {
              fontFamily: "var(--primary-font) !important",
            },
          },
        },
        MuiPickersToolbar: {
          styleOverrides: {
            root: {
              color: "var(--primary-color) !important",
              backgroundColor: "#fff !important",
              "& .MuiPickersToolbar-penIcon": {
                color: "var(--primary-color) !important",
              },
              "& .MuiPickersLabel-label": {
                color: "var(--primary-color) !important",
              },
            },
          },
        },
        MuiDayPicker: {
          styleOverrides: {
            weekDayLabel: {
              color: "var(--primary-color)",
              fontFamily: "var(--primary-font) !important",
            },
          },
        },
        MuiDialogActions: {
          styleOverrides: {
            root: {
              "& .MuiButton-root": {
                color: "var(--primary-color) !important",
                fontFamily: "var(--primary-font) !important",
              },
            },
          },
        },
        MuiPickersCalendarHeader: {
          styleOverrides: {
            root: {
              color: "var(--primary-color) !important",
              fontFamily: "var(--primary-font) !important",
              fontSize: "1.2rem",
              fontWeight: "bold",
            },
          },
        },
      },
      typography: {
        fontFamily: "var(--primary-font) !important",
        fontWeight: "bold",
      },
    });

    return (
      <ThemeProvider theme={theme}>
        <Picker
          ref={ref}
          size={props.isLanding && "small"}
          components={{ OpenPickerIcon: openPickerIcon || CalendarMonthIcon }}
          OpenPickerButtonProps={{ sx: { color: `var(--primary-color)` } }}
          onAccept={props?.onAccept}
          onClose={props?.onClose}
          format="yyyy/MM/dd"
          {...config}
          PopperProps={{
            container: document.body,
            disablePortal: false,
            placement: "auto",
          }}
          inputProps={{
            ...config?.inputProps,
            readOnly: selectOnly,
            placeholder: config?.placeholder,
            sx: { fontSize: ".85rem", fontWeight: "600" },
          }}
          renderInput={(params) => (
            <TextField
              fullWidth
              size={props.isLanding && "small"}
              required={props.required}
              inputProps={{ readOnly: selectOnly }}
              sx={{
                ...(props.isLanding && {
                  "& .MuiInputBase-root": {
                    height: "45px",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    paddingBottom: "5px",
                    color: `#333`,
                    fontWeight: "600",
                  },
                  "& .MuiOutlinedInput-root": { legend: { display: "none" } },
                }),
                ...(!props.isLanding && {
                  "& .MuiOutlinedInput-root": {
                    height: "60px",
                    color: config.error ? "" : "#333",
                  },
                }),
                "& .MuiOutlinedInput-root.Mui-focused": {
                  "& > fieldset": {
                    border: readOnly && "1px solid #8282a1",
                    borderColor: "var(--primary-color)",
                  },
                },
                "& .MuiOutlinedInput-root:hover": {
                  "& > fieldset": {
                    border: readOnly && "1px solid #8282a1",
                    borderColor: "var(--primary-color)",
                  },
                },
              }}
              {...params}
              InputLabelProps={{
                sx: {
                  fontSize: ".9rem",
                  paddingTop: "5px",
                  color: config.error ? "red" : "#8282a1",
                  "&.Mui-focused": {
                    color: config.error ? "red" : "var(--primary-color)",
                  },
                },
              }}
              helperText={
                config.helperText ||
                (!noAgeWrapper &&
                  !props?.isLanding &&
                  (age !== null || ageInMonths !== null) &&
                  !error && (
                    <span style={{ color: "#8282a1" }}>
                      <span style={{ color: "#8282a1" }}>
                        <span style={{ color: "#8282a1" }}>
                          {ageInMonths !== null
                            ? `${ageInMonths} ${
                                ageInMonths === 1 ? "month" : "months"
                              }`
                            : age !== null
                            ? `${age} ${age === 1 ? "year" : "years"}`
                            : ""}
                        </span>
                      </span>
                    </span>
                  ))
              }
              error={config.error}
              placeholder={config.placeholder}
            />
          )}
          disableOpenPicker={readOnly}
        />
        {extraMsg && !error && <span>{extraMsg}</span>}
      </ThemeProvider>
    );
  }
);
export default DatePicker;
